import { useQuery } from "@tanstack/react-query";

import { Badge } from "@/components/Badge";
import { getResults } from "@/lib/api";
import { canonicalKey } from "@/lib/canonical";
import { useProfileLabel } from "@/lib/profileLabels";
import { useSelection } from "@/lib/selection";
import { wilson } from "@/lib/wilson";

import { Aggregate } from "./results/Aggregate";
import {
  PerQuerySection,
  type PerQueryUiRow,
  PerVoterSection,
  type PerVoterUiRow,
} from "./results/Breakdowns";
import { Header } from "./results/Header";
import { Latency } from "./results/Latency";

/**
 * Results page: reads the pair aggregate for the currently-selected
 * (mode, profileA, profileB) and renders Header / Aggregate / Latency /
 * PerQuery / PerVoter sections. The aggregate is canonical (keyed on
 * profile1 < profile2); this page is the one place that maps profile1/2
 * back to user-facing A/B labels.
 */
export function Results() {
  const { mode, profileA, profileB } = useSelection();
  const hasPair = !!(profileA && profileB);

  const key = canonicalKey(mode, profileA, profileB);
  const resultsQuery = useQuery({
    queryKey: ["results", key],
    queryFn: () => getResults({ mode, profileA: profileA as string, profileB: profileB as string }),
    enabled: hasPair,
    staleTime: 30_000,
  });

  if (!hasPair) return <NoPairPlaceholder />;
  if (resultsQuery.isLoading) return <LoadingSkeleton />;
  if (resultsQuery.isError) {
    return <ErrorState onRetry={() => void resultsQuery.refetch()} />;
  }

  const agg = resultsQuery.data;
  const totalVotes = agg
    ? agg.counts.profile1_better +
      agg.counts.profile2_better +
      agg.counts.both_good +
      agg.counts.both_bad
    : 0;
  if (!agg || totalVotes === 0) {
    return <NoVotesYet profileA={profileA!} profileB={profileB!} />;
  }

  // Canonical → user-slot mapping. profile1 is the lex-lower of (A, B); if A
  // happens to be profile1, profile1_better = "A better", etc.
  const aIsP1 = profileA === agg.profile1;
  const aBetter = aIsP1 ? agg.counts.profile1_better : agg.counts.profile2_better;
  const bBetter = aIsP1 ? agg.counts.profile2_better : agg.counts.profile1_better;
  const bothGood = agg.counts.both_good;
  const bothBad = agg.counts.both_bad;
  const total = aBetter + bBetter + bothGood + bothBad;
  const decided = aBetter + bBetter;
  const winRateA = decided > 0 ? aBetter / decided : null;
  const ciA = wilson(aBetter, decided);
  const latencyA = aIsP1 ? agg.latencyProfile1 : agg.latencyProfile2;
  const latencyB = aIsP1 ? agg.latencyProfile2 : agg.latencyProfile1;

  const perQuery: PerQueryUiRow[] = (agg.perQuery ?? [])
    .map((r) => ({
      queryText: r.queryText,
      aBetter: aIsP1 ? r.profile1_better : r.profile2_better,
      bBetter: aIsP1 ? r.profile2_better : r.profile1_better,
      both_good: r.both_good,
      both_bad: r.both_bad,
      total: r.profile1_better + r.profile2_better + r.both_good + r.both_bad,
    }))
    .sort((a, b) => b.total - a.total);

  const perVoter: PerVoterUiRow[] = (agg.perVoter ?? [])
    .map((r) => ({
      voterEmail: r.voterEmail,
      aBetter: aIsP1 ? r.profile1_better : r.profile2_better,
      bBetter: aIsP1 ? r.profile2_better : r.profile1_better,
      both_good: r.both_good,
      both_bad: r.both_bad,
      total: r.total,
    }))
    .sort((a, b) => b.total - a.total);

  return (
    <div style={{ flex: 1, overflow: "auto", padding: "24px 32px 48px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <Header
          profileA={profileA!}
          profileB={profileB!}
          mode={mode}
          lastVoteAt={agg.lastVoteAt}
          lastIndexVersion={agg.lastIndexVersion}
        />
        <Aggregate
          profileA={profileA!}
          profileB={profileB!}
          aBetter={aBetter}
          bBetter={bBetter}
          bothGood={bothGood}
          bothBad={bothBad}
          total={total}
          decided={decided}
          winRateA={winRateA}
          ciA={ciA}
        />
        <Latency
          profileA={profileA!}
          profileB={profileB!}
          latencyA={latencyA}
          latencyB={latencyB}
        />
        <PerQuerySection rows={perQuery} profileA={profileA!} profileB={profileB!} />
        <PerVoterSection rows={perVoter} profileA={profileA!} profileB={profileB!} />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Non-happy states
// -----------------------------------------------------------------------------

function NoPairPlaceholder() {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        gap: 18,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 12,
          border: "1px dashed var(--border)",
          display: "grid",
          placeItems: "center",
          color: "var(--muted-foreground)",
          fontFamily: "var(--font-mono)",
          fontSize: 18,
          fontWeight: 600,
        }}
      >
        A · B
      </div>
      <h1
        style={{
          margin: 0,
          fontSize: 22,
          fontWeight: 600,
          letterSpacing: "-0.01em",
        }}
      >
        Pick two profiles to view results
      </h1>
      <p
        style={{
          margin: 0,
          color: "var(--muted-foreground)",
          fontSize: 13,
          maxWidth: 440,
          textAlign: "center",
          lineHeight: 1.55,
        }}
      >
        Results are scoped to the pair you've selected in the sidebar.
      </p>
    </div>
  );
}

function NoVotesYet({
  profileA,
  profileB,
}: {
  profileA: string;
  profileB: string;
}) {
  const label = useProfileLabel();
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        gap: 14,
      }}
    >
      <Badge variant="outline">no votes yet</Badge>
      <h1
        style={{
          margin: 0,
          fontSize: 22,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          textAlign: "center",
        }}
      >
        No votes for{" "}
        <span
          style={{
            fontFamily: "var(--font-mono)",
            color: "var(--atf-pink-700)",
          }}
        >
          {label(profileA)}
        </span>
        <span
          style={{
            margin: "0 8px",
            color: "var(--muted-foreground)",
            fontWeight: 400,
          }}
        >
          vs
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            color: "var(--atf-blue-950)",
          }}
        >
          {label(profileB)}
        </span>
      </h1>
      <p
        style={{
          margin: 0,
          color: "var(--muted-foreground)",
          fontSize: 13,
          maxWidth: 420,
          textAlign: "center",
          lineHeight: 1.55,
        }}
      >
        Run a search to start comparing. Aggregates accrue as votes come in — the first one ships a
        row here.
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  // Four stacked shimmer blocks matching the real layout: Aggregate card,
  // Latency card, PerQuery table, PerVoter table. The global CSS
  // prefers-reduced-motion rule disables the shimmer for users who opt out.
  const shimmerStyle = {
    borderRadius: 12,
    border: "1px solid var(--border)",
    background:
      "linear-gradient(90deg, var(--secondary), color-mix(in oklch, var(--secondary) 50%, var(--background)), var(--secondary))",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.4s linear infinite",
  } as const;
  return (
    <div style={{ flex: 1, overflow: "auto", padding: "24px 32px 48px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gap: 18 }}>
        <div style={{ ...shimmerStyle, height: 120 }} />
        <div style={{ ...shimmerStyle, height: 80 }} />
        <div style={{ ...shimmerStyle, height: 240 }} />
        <div style={{ ...shimmerStyle, height: 240 }} />
      </div>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        gap: 14,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 999,
          background: "color-mix(in oklch, var(--destructive) 15%, var(--background))",
          color: "var(--destructive)",
          display: "grid",
          placeItems: "center",
          fontSize: 18,
          fontWeight: 700,
        }}
      >
        !
      </div>
      <h1
        style={{
          margin: 0,
          fontSize: 20,
          fontWeight: 600,
          letterSpacing: "-0.01em",
        }}
      >
        Couldn't load results
      </h1>
      <p
        style={{
          margin: 0,
          color: "var(--muted-foreground)",
          fontSize: 13,
          lineHeight: 1.55,
        }}
      >
        This is usually temporary.
      </p>
      <button
        type="button"
        onClick={onRetry}
        style={{
          marginTop: 4,
          padding: "8px 14px",
          border: "1px solid var(--border)",
          borderRadius: 8,
          background: "var(--background)",
          fontSize: 13,
          fontFamily: "var(--font-sans)",
          cursor: "pointer",
          color: "var(--foreground)",
        }}
      >
        Retry
      </button>
    </div>
  );
}
